// src/hooks/useWorkspaceGroups.js
import {useEffect, useState} from 'react';
import {useGroupedJobs} from './useGroupedJobs';
import {useGroupedByWorkspace} from './useGroupedByWorkspace';

// Global cache for workspace metadata (shared across all hook instances)
const metadataCache = new Map();

export const useWorkspaceGroups = (stackedJobs, userId, apiUrl) => {
  // 1. Group projects by workspace (without metadata)
  const { projects, ungroupedJobs } = useGroupedJobs(stackedJobs);
  const { workspaces: baseWorkspaces } = useGroupedByWorkspace(projects);

  // 2. State for enriched workspaces and loading status
  const [workspacesWithMeta, setWorkspacesWithMeta] = useState([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState(null);

  // 3. Fetch workspace metadata from API (once per userId)
  useEffect(() => {
    if (!userId || !apiUrl) return;

    const fetchMetadata = async () => {
      setLoadingMetadata(true);
      setMetadataError(null);

      // Check cache first
      const cacheKey = `workspaceMetadata_${userId}`;
      if (metadataCache.has(cacheKey)) {
        const cachedMap = metadataCache.get(cacheKey);
        enrichWorkspaces(cachedMap);
        setLoadingMetadata(false);
        return;
      }

      try {
        const response = await fetch(
          `${apiUrl}/api/SiteNote/GetStackedWorkspaces?pageNumber=1&pageSize=100&userId=${userId}`
        );
        if (!response.ok) throw new Error('Failed to fetch workspace metadata');
        const data = await response.json();

        // Create a Map: workspaceId -> metadata object
        const metadataMap = new Map();
        (data.workspaces || []).forEach(ws => {
          metadataMap.set(ws.workspaceId, ws);
        });

        metadataCache.set(cacheKey, metadataMap);
        enrichWorkspaces(metadataMap);
      } catch (err) {
        console.error('Error fetching workspace metadata:', err);
        setMetadataError(err);
        // Fallback: use base workspaces without extra metadata
        setWorkspacesWithMeta(baseWorkspaces);
      } finally {
        setLoadingMetadata(false);
      }
    };

    const enrichWorkspaces = (metadataMap) => {
      const enriched = baseWorkspaces.map(workspace => {
        const meta = metadataMap.get(workspace.workspaceId);
        if (meta) {
          return {
            ...workspace,
            totalSiteNotes: meta.totalSiteNotes,
            latestTimeStamp: meta.latestTimeStamp,
            lastSiteNote: meta.lastSiteNote,
            lastSiteNoteUserName: meta.lastSiteNoteUserName,
            workspaceFriendlyId: meta.workspaceFriendlyId,
          };
        }
        return workspace;
      });
      setWorkspacesWithMeta(enriched);
    };

    fetchMetadata();
  }, [apiUrl, userId, baseWorkspaces]);

  // 4. Return enriched workspaces + ungrouped jobs + loading/error states
  return {
    workspaces: workspacesWithMeta,
    ungroupedJobs,
    loadingMetadata,
    metadataError,
  };
};